/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/*
 * While onboarding another app in cjm.js, please identify
 * if there are sharable configs among apps and if yes, then
 * create a baseConfig object and move common configs there
*/
import {HISTORY, RUNTIME, Solution} from '@exc/core/src/models/Solution';
import {platformCommon} from './experiencePlatform';

const sideNav = {workHubId: 'CJM'};

const baseConfig = {
  appRoot: 'cjm-home',
  access: {
    code: ['acp'],
    codeExt: {
      code: 'bso',
      fi: 'cjm_foundation'
    },
    codeFlagLogicOp: 'AND',
    codeLogicOp: 'AND',
    sandboxFlag: 'cjm-enabled-alpha'
  },
  analytics: {
    code: 'aep',
    scripts: {
      dev: '//assets.adobedtm.com/a7d65461e54e/e52b30f722ba/launch-adb0901820ad-development.min.js',
      qa: '//assets.adobedtm.com/a7d65461e54e/e52b30f722ba/launch-adb0901820ad-development.min.js',
      stage: '//assets.adobedtm.com/a7d65461e54e/e52b30f722ba/launch-028c418c7b9d-staging.min.js',
      prod: '//assets.adobedtm.com/a7d65461e54e/e52b30f722ba/launch-7db373b2abde.min.js'
    }
  },
  gainsight: {productKey: 'AP-GLVNMB5BLV8T-2'},
  nps: {enabled: true},
  parent: 'CJM',
  restrictBySandbox: true,
  sideNav,
  unifiedSearch: {
    enabled: true,
    featureFlag: 'cjm-global-search-enabled'
  },
  urlContext: {key: 'sandbox'}
};

export const cjmHome: Solution = {
  ...baseConfig,
  appId: 'cjm-home',
  distributionList: ['Grp-TrojanRabbit'],
  path: '/journey-optimizer/home',
  redirectFrom: [
    {path: '/journey-optimizer', options: {end: true}},
    {path: '/cjm/home'},
    {path: '/cjm', options: {end: true}}
  ],
  spaAppId: 'exc-cjm-home',
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:12399'
    }
  }
};

export const cjmAssets: Solution = {
  ...baseConfig,
  appId: 'cjm-assets',
  distributionList: ['ORG-VUROSEV-ALL@adobe.com'],
  metricsAppId: 'cjm-assets',
  path: '/journey-optimizer/assets',
  redirectFrom: [{path: '/cjm/assets'}],
  sandbox: {
    hideInitialSpinner: true,
    history: HISTORY.HASH,
    ims: {
      client_id: 'aem-assets-frontend-1',
      scope: 'AdobeID,openid,read_organizations,ab.manage,additional_info.projectedProductContext'
    },
    sources: {
      dev: 'https://localhost.corp.adobe.com:8443/',
      qa: 'https://experience-qa.adobe.com/solutions/ddam',
      stage: 'https://experience-stage.adobe.com/solutions/ddam',
      prod: 'https://experience.adobe.com/solutions/ddam'
    }
  },
  unifiedSearch: {...baseConfig.unifiedSearch, compact: true}
};

export const cjmAdmin: Solution = {
  ...baseConfig,
  appId: 'cjm-admin',
  distributionList: ['GRP-VOYAGER'],
  path: '/journey-optimizer/admin',
  redirectFrom: [{path: '/cjm/admin'}],
  runtime: RUNTIME.CDN,
  sandbox: {
    history: HISTORY.HISTORY,
    ims: {
      client_id: 'voyager',
      scope: 'AdobeID,openid,additional_info.projectedProductContext,read_organizations'
    },
    pathPrefix: '/admin',
    sources: {
      dev: 'https://localhost.corp.adobe.com/admin',
      qa: 'https://journeys-dev.adobe.com/admin',
      stage: 'https://journeys-stage.adobe.com/admin',
      prod: 'https://journeys.adobe.com/admin'
    }
  }
};

export const cjmPlatform: Solution = {
  ...baseConfig,
  ...platformCommon,
  appId: 'cjm-platform',
  appRoot: 'cjm-home',
  parent: 'CJM',
  path: '/journey-optimizer/platform',
  redirectFrom: [{path: '/cjm/platform'}],
  sideNav // This is necessary because the platformCommon will override the setting in baseConfig. This will be a problem when AEP has a sideNav.
};

export const cjmMessage: Solution = {
  ...baseConfig,
  appId: 'cjm-message',
  distributionList: ['Grp-Content-Authoring-India'],
  path: '/journey-optimizer/messages',
  redirectFrom: [{path: '/cjm/messages'}],
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:8443'
    }
  },
  spaAppId: 'cjm-message-ui'
};

export const cjmLiveReport: Solution = {
  ...baseConfig,
  appId: 'cjm-live-report',
  distributionList: ['Grp-cjm-live-reporting-ui-team'],
  path: '/journey-optimizer/reports',
  redirectFrom: [{path: '/cjm/livereports'}, {path: '/journey-optimizer/livereports'}],
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:3000'
    },
    ims: {
      client_id: 'cjm-live-report',
      scope: 'AdobeID, openid, read_organizations, ab.manage, additional_info, additional_info.projectedProductContext, additional_info.roles, read_pc.acp, read_pc, read_pc.dma_tartan'
    }
  },
  spaAppId: 'cjm-live-reporting-ui'
};

// The solution config for the Journeys Orchestration app
export const cjmJourneys: Solution = {
  ...baseConfig,
  appId: 'cjm-journeys',
  distributionList: ['GRP-VOYAGER'],
  path: '/journey-optimizer/journeys',
  redirectFrom: [{path: '/cjm/journeys'}],
  runtime: RUNTIME.CDN,
  sandbox: {
    history: HISTORY.HISTORY,
    ims: {
      client_id: 'voyager',
      scope: 'AdobeID,openid,additional_info.projectedProductContext,read_organizations'
    },
    sources: {
      dev: 'https://localhost.corp.adobe.com/',
      qa: 'https://journeys-dev.adobe.com/',
      stage: 'https://journeys-stage.adobe.com/',
      prod: 'https://journeys.adobe.com/'
    }
  }
};

export const cjmEmailDesigner: Solution = {
  ...baseConfig,
  appId: 'cjm-email-designer',
  distributionList: ['ORG-FINA-ALL@adobe.com'],
  path: '/journey-optimizer/content/email-designer',
  redirectFrom: [{path: '/cjm/email-designer'}, {path: '/cjm/content/email-designer'}],
  spaAppId: 'pixel-acrites-ui',
  sandbox: {
    hideInitialSpinner: true,
    history: HISTORY.HASH,
    sources: {
      dev: 'https://localhost.rd.campaign.adobe.com:9000/'
    }
  },
  sideNav: {...baseConfig.sideNav, closeOnLoad: true}
};

export const cjmConfigUi: Solution = {
  ...baseConfig,
  appId: 'cjm-configui',
  distributionList: ['Grp-cjm-config-engg'],
  path: '/journey-optimizer/configui',
  redirectFrom: [{path: '/cjm/configui'}],
  spaAppId: 'exc-cjm-config-ui',
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:8013'
    }
  }
};
